/*
 * Order submit failures come back as `{domainCode}-{failureCode}` (see CollaterateErrors.java and
 * ProjectErrorCode.java). Each maps to the checkout step a consumer should send the user back to.
 *
 * The API includes a `detail` string with these codes. For CARD_NOT_AUTHORIZED that detail is
 * Braintree's raw decline reason, which should be logged rather than displayed - distinguishing
 * "Insufficient Funds" from "CVV Verification Failed" is useful feedback for card testing. Use the
 * generic message below for anything user facing.
 */

/** The checkout step a consumer should return the user to in order to recover from an error. */
export const checkoutSteps = Object.freeze({
    CONTACT: 'CONTACT',
    SHIPPING: 'SHIPPING',
    PAYMENT: 'PAYMENT',
    REVIEW: 'REVIEW',
});

const checkoutErrorCodes = Object.freeze({
    NO_SHIPMENT: '1-102',
    INVALID_STATE: '1-103',
    NO_SHIPPING_SERVICE: '1-104',
    MISSING_ORDER_METADATA: '1-105',
    CARD_NOT_AUTHORIZED: '1-106',
    ORDER_ITEM_INVALID_STATE: '2-05',
});

export default checkoutErrorCodes;

const ERRORS_BY_CODE = Object.freeze({
    [checkoutErrorCodes.NO_SHIPMENT]: {
        step: checkoutSteps.SHIPPING,
        message: 'This order has no shipment. Please review your shipping details.',
    },
    [checkoutErrorCodes.INVALID_STATE]: {
        step: checkoutSteps.REVIEW,
        message: 'This cart is no longer valid and cannot be ordered. Please start a new cart.',
    },
    [checkoutErrorCodes.NO_SHIPPING_SERVICE]: {
        step: checkoutSteps.SHIPPING,
        message: 'Please choose a shipping method.',
    },
    [checkoutErrorCodes.MISSING_ORDER_METADATA]: {
        step: checkoutSteps.CONTACT,
        message: 'Some required order information is missing.',
    },
    [checkoutErrorCodes.CARD_NOT_AUTHORIZED]: {
        step: checkoutSteps.PAYMENT,
        message: 'Your payment could not be authorized. Please check your payment details or try another payment method.',
    },
    [checkoutErrorCodes.ORDER_ITEM_INVALID_STATE]: {
        step: checkoutSteps.REVIEW,
        message: 'One or more items in this order can no longer be ordered.',
    },
});

/**
 * Look up the recovery step and a consumer-safe message for an order submit error code.
 *
 * @param {string} code - The error code from a failed order submit, e.g. "1-106".
 * @returns {?Collaterate.Checkout~checkoutErrorInfo} The matching info, or null for an unrecognized code.
 */

export function getCheckoutErrorInfo (code) {
    const error = ERRORS_BY_CODE[code];

    return error
        ? Object.freeze({ code, step: error.step, message: error.message })
        : null;
}

/**
 * Whether a submitted nonce is now spent and must be replaced before retrying. Braintree payment
 * method nonces are single use, so a card authorization failure invalidates the stored nonce - the
 * consumer has to collect a fresh one rather than resubmit.
 *
 * @param {string} code - The error code from a failed order submit.
 * @returns {boolean}
 */

export function requiresNewPaymentMethod (code) {
    return code === checkoutErrorCodes.CARD_NOT_AUTHORIZED;
}

/**
 * Recovery information for a failed order submit.
 *
 * @typedef {Object} Collaterate.Checkout~checkoutErrorInfo
 * @property {string} code - The raw Collaterate error code, e.g. "1-106".
 * @property {string} step - Which {@link Collaterate.Checkout.checkoutSteps checkout step} to return to.
 * @property {string} message - A consumer-safe message. Never contains gateway decline detail.
 */
